import { HostClientType } from '@microsoft/teams-js';
import { LoadingSpinner } from '@momentum-ui/react-collaboration';
import { IWebexIntContact } from '@webex/component-adapter-interfaces/dist/esm/src';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DownloadWebex } from '../components/DownloadWebex/DownloadWebex';
import { WebexSignIn } from '../components/SignIn';
import { MicrosoftTeamsSDK } from '../ms-teams-sdk';
import { MsTeamsSDKAdapter } from '../ms-teams-sdk-adapter/MsTeamsSDKAdapter';
import { MsTeamsSearchMessageExtensionAdapter } from '../ms-teams-sdk-adapter/MsTeamsSearchMessageExtensionAdapter';
import { getMSAccessTokenPromise, getWebexAccessTokenPromise, getWebexTokens, makeAcallToken } from '../ms-teams-sdk/service/MsTeamsAuthenticationService';
import { getMembersInChat } from '../ms-teams-sdk/service/MsTeamsMessageExtensionService';
import { getUsersByIds } from '../ms-teams-sdk/service/MsTeamsUserService';
import { getLocalStorageItem, setLocalStorageItem } from '../ms-teams-sdk/util/LocalStorageUtil';
import { getMSTeamsContext } from '../ms-teams-sdk/util/MsTeamsContextUtil';
import { transformGraphUser } from '../ms-teams-sdk/util/MsTeamsGraphUserUtil';
import { MAKE_A_CALL_KEYS } from '../packages/adapter-interfaces/src';
import {
  AdapterProvider,
  IAdapterContext,
  useMakeCall,
  WebexSearchContacts,
  WebexSearchContactsHandle
} from '../packages/widgets/src';
import { ContactCard } from '../packages/widgets/src/components/ContactCard';
import { initWebex } from '../packages/widgets/src/services/WebexInstanceService';
import { IChannelData } from '../packages/widgets/src/utils/CallBackUrl';
import { FIRST_TIME_DOWNLOAD } from '../packages/widgets/src/utils/MetricUtils';
import './MessageExtension.scss';
import { MobileChatExtension } from './MobileChatExtension';
import { MobileLandingPage } from './MobileLandingPage';
/**
 * Main landing page
 *
 * @returns {JSX.Element} React element containing the tab pag
 */
export const MessageExtension = () => {
  const [shouldShowSignIn, setShouldShowSignIn] = useState<boolean>();
  const [chatMembers, setChatMembers] = useState<IWebexIntContact[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const searchContactsRef = useRef<WebexSearchContactsHandle>(null);
  const [makeACallToken, setMakeACallToken] = useState<string>('');
  const [clientType, setClientType] = useState<string>('');
  const [context, setMsTeamsContext] = useState<any>();

  const fetchClientType = async () => {
    const context = await getMSTeamsContext();
    const clientType = context?.app?.host?.clientType;
    
    setMsTeamsContext(context);
    setClientType(clientType);
    setLoading(false);
  };

  const [makeCall] = useMakeCall();

  const msTeamsSearchMessageExtension = useMemo(
    () => new MsTeamsSearchMessageExtensionAdapter(),
    []
  );
  const messageExtensionAdapter: IAdapterContext = useMemo(
    () => ({
      ...MsTeamsSDKAdapter,
      searchContactsAdapter: msTeamsSearchMessageExtension,
    }),
    [msTeamsSearchMessageExtension]
  );

  const getLoginDetails = async () => {
    try {
      const msTokenAvailable = await getMSAccessTokenPromise();
      const webexTokenAvailable = await getWebexAccessTokenPromise();
      if(msTokenAvailable || webexTokenAvailable) {
        setShouldShowSignIn(true);
      }else {
        await reInitWebexInstance()
        await makeAcallToken();
        setShouldShowSignIn(false);
     }
    }
    catch(error) {
      throw error;
    }
  }

  const reInitWebexInstance = async () => {
    try {
      const webexTokens = await getWebexTokens(false);
      if(!(window?.isWebexInitalized)) {
        initWebex(webexTokens?.access_token as string)
      }
    }
    catch(error) {
      throw error;
    }
  }

  const loadUsersInChat = async () => {
    setLoading(true);
    const graphMembers = await getMembersInChat();
    const userIds: string[] = graphMembers.map(graphMember => graphMember.userId);
    const hydratedGraphMembers =  await getUsersByIds(userIds);
    const webexInt: IWebexIntContact[] = hydratedGraphMembers.map(
      (graphMember) =>
        transformGraphUser(graphMember, () =>
          MicrosoftTeamsSDK.user.fetchDirectoryUserAvatar(graphMember.id)
        )
    );
    setChatMembers(webexInt);
    setLoading(false);
  };

  useEffect(() => {
    fetchClientType();
    loadUsersInChat();
    getLoginDetails();
  }, []);

  useEffect(() => {
    msTeamsSearchMessageExtension.chatMembers = chatMembers || [];
  }, [chatMembers, msTeamsSearchMessageExtension]);

  useEffect(() => {
    const handleLocalStorageChange = (event: StorageEvent) => {
      const stringValue = getLocalStorageItem<string>(MAKE_A_CALL_KEYS.MAKE_A_CALL_TOKEN);
      if (stringValue) {
        setMakeACallToken(stringValue);
      } else {
        setMakeACallToken('');
      }
    };
    window.addEventListener('storage', handleLocalStorageChange);

    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
    };
  }, []);
  
  if (loading || !chatMembers) {
    return (
      <div className="message-extension message-extension--loading">
        <LoadingSpinner scale={48} />
      </div>
    );
  } else if(shouldShowSignIn) {
    return (
      <div className={clientType!== HostClientType.desktop && clientType!== HostClientType.web? "message-extension__mobile": "message-extension"}>
        <WebexSignIn></WebexSignIn>
      </div>
    );
  }
  // Decide which Download Webex Screen to show based on the clientType
  if(clientType !== HostClientType.desktop && clientType !== HostClientType.web) {
    if(getLocalStorageItem(FIRST_TIME_DOWNLOAD)=== null) {
      return(
        <DownloadWebex clientType = {clientType} src = 'chatExtension'></DownloadWebex>
      );  
    }
    const child = document.querySelector('.mobile-extension');
    const parent = child?.parentElement;
    parent?.classList.add('mobile-extension__parent');
    let channelData: IChannelData = {};
    if(chatMembers.length !== 1) {
          channelData = {    
          channelId: context?.channel?.id,
          channelName: context?.channel?.displayName,
          tenantId: context?.user?.tenant.id,
          groupId: context?.team?.groupId,
        }
      }
      return (
        chatMembers.length === 1 ? (
          <div className="message-extension mobile-extension">
            <MobileChatExtension user={chatMembers[0]} clientType = {clientType}
            />
            </div>
          ) : (
            <MobileLandingPage src='chatExtension' user={chatMembers} channelData = {channelData} clientType = {clientType}></MobileLandingPage>
          )
      );
    }
else {
  // if(getLocalStorageItem(FIRST_TIME_DOWNLOAD)=== null) {
  //   return(
  //       <DownloadWebexDesktop clientType = {clientType} src = 'chatExtension'></DownloadWebexDesktop>
  //     );
  // }
  setLocalStorageItem(FIRST_TIME_DOWNLOAD, true);
  return (
    <div className="message-extension">
      {chatMembers.length === 1 ? (
        <ContactCard user={chatMembers[0]}
         makeCall = {makeCall}
        />
      ) : (
        <AdapterProvider adapter={messageExtensionAdapter}>
          <WebexSearchContacts
            ref={searchContactsRef}
            hideDropdownSource
            label="Search channel members"
          />
            <div className="message-extension__icon">
              <svg
                width="160"
                height="83"
                viewBox="0 0 160 83"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M118.475 17.4906C118.475 17.4906 119.747 21.4219 120.21 22.6937C120.788 24.4281 119.632 25.0062 118.475 25.0062H117.319M116.741 7.66248C112.694 7.66248 110.382 9.97498 108.069 12.2875C105.757 14.6 103.444 16.9125 99.9754 16.9125M126.569 10.5531C126.569 5.4656 122.407 1.3031 117.319 1.3031C114.428 1.3031 111.538 1.3031 111.538 1.3031C102.577 1.3031 95.3504 8.52966 95.3504 17.4906V20.3812C95.3504 23.85 93.0379 25.0062 91.8816 26.1625C90.7254 27.3187 88.4129 29.0531 88.4129 32.5219C88.4129 35.9906 90.7254 38.3031 90.7254 38.3031M74.5378 17.4906C74.5378 17.4906 75.8097 21.364 76.2722 22.6937C76.8503 24.4281 75.6941 25.0062 74.5378 25.0062H73.3816M76.2722 12.8656H66.4441C63.9003 12.8656 61.8191 14.9469 61.8191 17.4906V26.1625C61.8191 31.25 65.9816 35.4125 71.0691 35.4125C76.1566 35.4125 80.3191 31.25 80.3191 26.1625V15.1781C80.3191 8.00935 74.3644 2.2281 67.1379 2.45935C60.2582 2.6906 54.8816 8.58748 54.8816 15.4672V37.1468C54.8816 38.8812 53.8988 40.5 52.3379 41.3094L44.0707 45.4719C39.3878 47.8422 36.3816 52.6406 36.3816 57.9015V64.3765C36.3816 73.9734 28.6347 81.7203 19.0378 81.7203C9.44097 81.7203 1.69409 73.9734 1.69409 64.3765V57.9015C1.69409 52.6406 4.64253 47.8422 9.38316 45.4719L22.2753 38.9969C23.8363 38.1875 24.8191 36.6265 24.8191 34.8344V22.1156C24.8191 20.7859 23.6628 19.6875 22.2753 19.8031C21.0613 19.9187 20.1363 21.1328 20.1941 22.3468C20.4253 27.0297 27.941 35.4703 37.1332 35.4125C41.8738 35.3547 45.6316 31.4812 45.6316 26.7406V15.1781C45.6316 11.0156 42.2785 7.66248 38.116 7.66248C34.0691 7.66248 31.7566 9.97498 29.4441 12.2875C27.1316 14.6 24.8191 16.9125 20.7722 16.9125M39.8503 17.4906C39.8503 17.4906 41.1222 21.4219 41.5847 22.6937C42.1628 24.4281 41.0066 25.0062 39.8503 25.0062H38.6941M35.8035 4.19373C31.7566 4.19373 29.4441 6.50623 27.1316 8.81873C24.8191 11.1312 22.5066 13.4437 18.4597 13.4437M33.491 0.724976C29.4441 0.724976 27.1316 3.03748 24.8191 5.34998C22.5066 7.66248 20.1941 9.97498 16.1472 9.97498M116.799 7.66248C120.961 7.66248 124.314 11.0156 124.314 15.1781V26.7406C124.314 31.4812 120.557 35.3547 115.816 35.4125C106.682 35.5281 99.166 27.0875 98.8769 22.3469C98.8191 21.1328 99.7441 19.9187 100.958 19.8031C102.346 19.6875 103.502 20.7281 103.502 22.1156V34.8922C103.502 36.6265 102.519 38.2453 100.958 39.0547L88.066 45.5297C83.3832 47.9 80.3769 52.6984 80.3769 57.9593V64.4343C80.3769 74.0312 88.1238 81.7781 97.7207 81.7781C107.318 81.7781 115.064 74.0312 115.064 64.4343V57.9593C115.064 52.6984 118.013 47.9 122.753 45.5297L135.646 39.0547C137.207 38.2453 138.189 36.6844 138.189 34.8922V22.1156C138.189 20.7859 137.033 19.6875 135.646 19.8031C134.432 19.9187 133.507 21.1328 133.564 22.3469C133.796 27.0297 141.311 35.4703 150.503 35.4125C155.244 35.3547 159.002 31.4812 159.002 26.7406V13.7328C159.002 7.48904 154.146 2.11248 147.844 1.88123C141.253 1.64998 135.877 6.91091 135.877 13.4437C135.877 14.0797 136.397 14.6 137.033 14.6H138.189C140.733 14.6 142.814 16.6812 142.814 19.225V26.1625C142.814 28.0703 144.375 29.6312 146.283 29.6312C146.919 29.6312 147.439 29.1109 147.439 28.475C147.439 26.5672 149 25.0062 150.908 25.0062H153.221C154.377 25.0062 155.533 24.4281 154.955 22.6937C154.493 21.4219 153.221 17.4906 153.221 17.4906"
                  stroke="url(#paint0_linear_1414_792)"
                  strokeWidth="1.39559"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_1414_792"
                    x1="80.348"
                    y1="0.724976"
                    x2="80.348"
                    y2="81.7781"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#279BE8" />
                    <stop offset="1" stopColor="#875AE0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
        </AdapterProvider>
      )}
    </div>
  );
}};
